import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  FormGroup,
  IconChevronLeft,
  Select as SNSWSelect,
  AutoSuggestAddress,
  ComponentLoader,
  Input,
} from '@snsw-gel/react';
import { vars } from '@snsw-gel/theming';
import { debounce } from 'lodash';
import { getStorageItem } from 'app/sharedServices/storage/storage';
import { getBusinessList, getBusinessSuggestions } from 'app/services/api/internal/API';
import { nswPointBackendAPI } from 'app/services/api/external/nswPoint/nswPointApi';
import {
  StyledAddNewLink,
  StyledDropdown,
  StyledDropdownItem,
  StyledLinkGroup,
  StyledSearchContainer,
} from './BusinessSelect.styled';

interface Address {
  streetNumber?: string;
  streetName?: string;
  streetType?: string;
  buildingNumber?: string;
  unitNumber?: string;
  addressLine?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  country?: string;
  formattedAddress?: string;
  latitude?: number;
  longitude?: number;
  nswPointId?: string;
  propertyName?: string;
  stateElectoralPid: string;
  lgaPid?: string;
  lgaName?: string;
  lgaShortName?: string;
  validated?: boolean;
}
export interface BusinessSearchResult {
  businessName: string;
  identifier: string;
  identifierType: string;
  businessAddress: Address;
}

interface AddressValue {
  streetAddress: { value: string };
  suburb: { value: string };
  state: { value: string };
  postcode: { value: string };
  country: { value: string };
  formattedAddress: { value: string };
  latitude: { value: number };
  longitude: { value: number };
  lgaPid: { value: string };
  lgaName: { value: string };
  lgaShortName: { value: string };
}

interface BusinessSelectProps {
  onSelect: (business: BusinessSearchResult) => void;
  onClear: () => void;
  onSearchChange: (value: string) => void;
  businessAddress: string;
  onAddressChange: (address: AddressValue) => void;
  businessNameError?: string;
  businessAddressError?: string;
  addressHelpMessage?: string;
  // Optional initial business name used to seed the search field when returning
  // to this component with a previously entered/selected business (e.g. editing
  // from a review page). When omitted, the component behaves as before: the
  // search field starts empty and the saved-business dropdown is shown first.
  businessName?: string;
}

const getDisplayBusinessValue = (business: BusinessSearchResult) => {
  return `${business.businessName} (${business.identifierType.toUpperCase()}: ${business.identifier})`;
};

const toAddressValue = (address: Address) => {
  const addressValue: AddressValue = {
    streetAddress: { value: address?.streetNumber ? `${address.streetNumber} ${address.streetName}` : '' },
    suburb: { value: address?.suburb || '' },
    state: { value: address?.state || '' },
    postcode: { value: address?.postcode || '' },
    country: { value: address?.country || 'Australia' },
    formattedAddress: { value: address?.formattedAddress || '' },
    latitude: { value: address?.latitude },
    longitude: { value: address?.longitude },
    lgaPid: { value: address?.lgaPid },
    lgaName: { value: address?.lgaName },
    lgaShortName: { value: address?.lgaShortName },
  };
  return addressValue;
};

const MIN_CHARS_TO_SEARCH = 3;

const BusinessSelect: React.FC<BusinessSelectProps> = ({
  onSelect,
  onClear,
  onSearchChange,
  businessAddress,
  onAddressChange,
  businessNameError,
  businessAddressError,
  addressHelpMessage = 'Start typing to search for business address or you can enter it manually.',
  businessName = '',
}) => {
  const [businesses, setBusinesses] = useState<BusinessSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedValue, setSelectedValue] = useState('');
  // When an initial business name is provided, open the search view so the
  // seeded name is visible (a restored free-text/searched name won't necessarily
  // match a saved-business dropdown option). With no initial name this stays
  // false, preserving the default "show the dropdown first" behaviour.
  const [showAddNew, setShowAddNew] = useState(!!businessName);
  const [manualBusinessName, setManualBusinessName] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);

  // Search state
  const [searchValue, setSearchValue] = useState(businessName);
  const [suggestions, setSuggestions] = useState<BusinessSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchBusinessProfile = async () => {
      setIsLoading(true);
      const token = getStorageItem('accessToken', 'session');
      const sid = getStorageItem('sid', 'local');
      try {
        const existingBusinesses = token && sid ? await getBusinessList(token, sid) : {};
        setBusinesses(existingBusinesses?.data?.businesses ?? []);
      } catch (e) {
        console.log('fetchBusinessProfile - error', e);
      }
      setIsLoading(false);
    };
    fetchBusinessProfile();
  }, []);

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedValue(value);
    setShowAddNew(false);
    const selected = businesses.find((b) => getDisplayBusinessValue(b) === value);
    if (selected) {
      console.log('Set selected business address', selected.businessAddress);
      setAddressValue(toAddressValue(selected.businessAddress));
      onSelect(selected);
    }
  };

  const handleAddNewClick = () => {
    const emptyAddress = {
      streetAddress: { value: '' },
      suburb: { value: '' },
      state: { value: '' },
      postcode: { value: '' },
      country: { value: 'Australia' },
      formattedAddress: { value: '' },
      latitude: { value: undefined },
      longitude: { value: undefined },
      lgaPid: { value: '' },
      lgaName: { value: '' },
      lgaShortName: { value: '' },
    };

    setShowAddNew(true);
    setSelectedValue('');
    setAddressValue(emptyAddress);
    onAddressChange(emptyAddress);
    onClear();
  };

  const handleBackToSelection = () => {
    setShowAddNew(false);
    setSearchValue('');
    setSuggestions([]);
    setShowSearchDropdown(false);
    onClear();
  };

  // Search functionality
  const fetchSuggestions = useCallback(
    debounce(async (query: string) => {
      if (query.length < MIN_CHARS_TO_SEARCH) {
        setSuggestions([]);
        setShowSearchDropdown(false);
        return;
      }

      setIsSearching(true);
      try {
        const response = await getBusinessSuggestions(query);
        const results = response?.data?.results ?? [];
        const suggestions = results.map((result) => {
          const identifierType = result.abn ? 'abn' : 'acn';
          return {
            businessName: result.displayName,
            identifierType,
            identifier: result[identifierType],
            businessAddress: null,
          };
        });
        setSuggestions(suggestions);
        setShowSearchDropdown(results.length > 0);
      } catch (e) {
        setSuggestions([]);
        setShowSearchDropdown(false);
      } finally {
        setIsSearching(false);
      }
    }, 300),
    [],
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    onSearchChange(value);
    fetchSuggestions(value);
  };

  const handleManualBusinessNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setManualBusinessName(value);
    // Manual entry: only the free-text business name is captured (no ABN/ACN, no validation).
    onSearchChange(value);
  };

  const handleSearchSelect = (business: BusinessSearchResult) => {
    setSearchValue(`${business.businessName} (${business.identifierType}: ${business.identifier})`);
    setShowSearchDropdown(false);
    onSelect(business);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Address state
  const initialAddressValue: AddressValue = {
    streetAddress: { value: '' },
    suburb: { value: '' },
    state: { value: '' },
    postcode: { value: '' },
    country: { value: 'Australia' },
    formattedAddress: { value: businessAddress || '' },
    latitude: { value: undefined },
    longitude: { value: undefined },
    lgaPid: { value: '' },
    lgaName: { value: '' },
    lgaShortName: { value: '' },
  };

  const [addressValue, setAddressValue] = useState(initialAddressValue);

  useEffect(() => {
    if (businessAddress) {
      setAddressValue((prev) => ({
        ...prev,
        formattedAddress: { value: businessAddress },
      }));
    }
  }, [businessAddress]);

  const handleAddressSelect = (address: AddressValue) => {
    setAddressValue(address);
    onAddressChange(address);
  };

  const handleAddressChange = (address: AddressValue) => {
    setAddressValue(address);
    onAddressChange(address);
  };

  const dropdownOptions = businesses.map((business) => ({
    value: getDisplayBusinessValue(business),
    text: getDisplayBusinessValue(business),
  }));

  const hasBusinessOptions = dropdownOptions.length > 0;

  if (isLoading) {
    return <ComponentLoader label="Loading business profile..." />;
  }

  return (
    <div>
      {hasBusinessOptions && !showAddNew ? (
        <>
          <div style={{ maxWidth: `${vars.layouts.inputWidths['xl']}` }}>
            <FormGroup
              id="businessSelect"
              label="Select a business from your business profile"
              helpMessage="Choose a business from you have previously saved"
              errorMessage={businessNameError}
              hasError={!!businessNameError}
            >
              <SNSWSelect options={dropdownOptions} value={selectedValue} onChange={handleSelectChange} />
            </FormGroup>
          </div>

          <StyledAddNewLink type="button" onClick={handleAddNewClick}>
            + Add new business
          </StyledAddNewLink>
        </>
      ) : (
        <div ref={searchContainerRef}>
          {showManualEntry ? (
            <>
              <div style={{ maxWidth: '100%' }}>
                <FormGroup id="manualBusinessName" label="Business name (optional)">
                  <Input
                    id="manualBusinessName"
                    type="text"
                    value={manualBusinessName}
                    onChange={handleManualBusinessNameChange}
                  />
                </FormGroup>
              </div>

              <Button
                variant="link"
                iconStart={<IconChevronLeft />}
                onClick={() => setShowManualEntry(false)}
                style={{ marginTop: '1rem' }}
              >
                Back to search
              </Button>
            </>
          ) : (
            <>
              <StyledSearchContainer>
                <FormGroup
                  id="businessSearch"
                  label="Search business (optional)"
                  helpMessage="Search the business name, 9 digit ACN or 11 digit ABN, with no spaces."
                  errorMessage={businessNameError}
                  hasError={!!businessNameError}
                >
                  <Input
                    id="businessSearch"
                    type="text"
                    value={searchValue}
                    onChange={handleSearchChange}
                    placeholder="Search by business name, ABN or ACN"
                    aria-autocomplete="list"
                    aria-expanded={showSearchDropdown}
                  />
                </FormGroup>
                {(isSearching || showSearchDropdown) && (
                  <StyledDropdown role="listbox" aria-label="Business suggestions">
                    {isSearching ? (
                      <StyledDropdownItem>Loading...</StyledDropdownItem>
                    ) : (
                      suggestions.map((business) => (
                        <StyledDropdownItem
                          key={getDisplayBusinessValue(business)}
                          role="option"
                          onClick={() => handleSearchSelect(business)}
                          onKeyDown={(e) => e.key === 'Enter' && handleSearchSelect(business)}
                          tabIndex={0}
                        >
                          {getDisplayBusinessValue(business)}
                        </StyledDropdownItem>
                      ))
                    )}
                  </StyledDropdown>
                )}
              </StyledSearchContainer>

              {/* Secondary actions: manual entry is always available as a
                  fallback; "Back to business selection" appears only when the
                  user has saved businesses to return to. */}
              <StyledLinkGroup>
                <StyledAddNewLink type="button" onClick={() => setShowManualEntry(true)}>
                  Enter business name manually
                </StyledAddNewLink>

                {hasBusinessOptions && (
                  <Button variant="link" iconStart={<IconChevronLeft />} onClick={handleBackToSelection}>
                    Back to business selection
                  </Button>
                )}
              </StyledLinkGroup>
            </>
          )}
        </div>
      )}

      <div style={{ maxWidth: '100%', marginTop: '1rem' }}>
        <AutoSuggestAddress
          apiOptions={{
            outFields: ['lgaPid', 'lgaName', 'lgaShortName'],
            stateTerritory: ['NSW'],
          }}
          backendAPI={nswPointBackendAPI}
          id="businessAddress"
          value={addressValue}
          onSelect={handleAddressSelect}
          onChange={handleAddressChange}
          label="Business address (optional)"
          helpMessage={addressHelpMessage}
          persistentAddress="both"
        />
        {businessAddressError && (
          <span
            className="field-error"
            style={{ color: '#B81237', fontSize: '1rem', display: 'block', marginTop: '0.5rem' }}
          >
            {businessAddressError}
          </span>
        )}
      </div>
    </div>
  );
};

export default BusinessSelect;
